import type { ReactNode } from 'react';

interface Props {
  // Short uppercase line above the title. Mockup convention:
  //   `SOCIÉTÉ · {SLUG.UPPER}` for per-tenant pages
  //   `ADMINISTRATION · GLOBAL` for admin pages
  //   `SOCIÉTÉ · {SLUG.UPPER} · STATISTIQUES` for sub-routes
  eyebrow: string;
  // Page title — renders with the trailing underscore-accent in coral
  // matching the mockup's "title_" pattern.
  title: ReactNode;
  // Short descriptive subtitle. May contain JSX (counts, links).
  subtitle?: ReactNode;
  // Trailing slot for header-right actions (refresh, filters, etc.).
  // Kept optional so screens can ship the identity without changing
  // existing action toolbars.
  actions?: ReactNode;
  testId?: string;
}

// REQ-FE-029i — Reusable page-identity header. Consistent pattern across
// all screens that have a meaningful title:
//   <PageIdentity eyebrow="…" title="…" subtitle="…" />
// Eyebrows + underscore-accent + subtitle copy come from the mockup
// (`02-tenant-aif-overview.png` / `05-stats.png` / `09-admin-members.png`).
export function PageIdentity({ eyebrow, title, subtitle, actions, testId }: Props) {
  return (
    <header className="page-identity" data-testid={testId ?? 'page-identity'}>
      <div className="page-identity__main">
        <div
          className="eyebrow page-identity__eyebrow"
          data-testid={`${testId ?? 'page-identity'}-eyebrow`}
        >
          {eyebrow}
        </div>
        <h1
          className="display page-identity__title"
          data-testid={`${testId ?? 'page-identity'}-title`}
        >
          {title}
          <span className="page-identity__title-accent" aria-hidden="true">
            _
          </span>
        </h1>
        {subtitle ? (
          <p
            className="muted page-identity__subtitle"
            data-testid={`${testId ?? 'page-identity'}-subtitle`}
          >
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? <div className="page-identity__actions">{actions}</div> : null}
    </header>
  );
}

// Helper: turn a tenant legal name into the mockup's `SLUG` style.
//   "Altyor Industrie France" → "ALTYOR-INDUSTRIE-FRANCE"
export function tenantSlug(legalName: string): string {
  return legalName.toUpperCase().replace(/\s+/g, '-');
}
